<template>
    <div class="trend-report-date-info">
        <div class="date-handle-box">
            <p class="left-handle-icon" :class="{ disabled: isPrevDisabled }" @click="onClickPrev"></p>
            <p class="date-text h1">{{ computedReportTitle }}</p>
            <p class="right-handle-icon" :class="{ disabled: isNextDisabled }" @click="onClickNext"></p>
        </div>
        <div class="date-info-box">
            <p class="report-period-wrap">
                <span class="label">{{ t('trend_report_report_label_period_text') }}: </span>
                <span class="value">{{ computedReportPeriod }}</span>
            </p>
            <p class="inspection-num-wrap">
                <span class="label">{{ t('trend_report_report_label_inspection_text') }}: </span>
                <!-- TODO: 추후 메타데이터의 count 값으로 변경 -->
                <!-- <span class="value">{{ props.timeseriesData?.MeasureCount ?? 0 }}{{ t('all_day') }}</span> -->
                <span class="value"
                    >{{ props.timeseriesData?.MonthReportMetaInfo?.MeasureCountData?.Count ?? 0
                    }}{{ t('all_day') }}</span
                >
            </p>
            <p class="publish-date-wrap">
                <span class="label">{{ t('trend_report_report_label_publish_text') }}: </span>
                <span class="value">{{ computedPublishDate }}</span>
            </p>
        </div>
        <div class="status-box" v-if="props.moduleData?.MonthReportMetaInfo">
            <div
                class="status-box-inner body-2--regular"
                :style="{
                    backgroundColor: props.moduleData?.MonthReportMetaInfo?.MeasureCountData.BackgroundColor ?? '',
                }"
                v-if="props.moduleData?.MonthReportMetaInfo?.MeasureCountData"
                @click="handleClickStatus('MEASURE_COUNT')"
            >
                <div
                    class="status-box-inner-item-label"
                    :style="
                        props.moduleData.MonthReportMetaInfo?.MeasureCountData.CountStatus ===
                            t('trend_report_report_status_perfect') &&
                        props.moduleData?.MonthReportMetaInfo?.MeasureCountData.TextColor
                            ? {
                                  color: props.moduleData?.MonthReportMetaInfo?.MeasureCountData.TextColor,
                              }
                            : {}
                    "
                >
                    검사
                </div>
                <div
                    class="status-box-inner-item-text"
                    :style="{
                        color: props.moduleData?.MonthReportMetaInfo?.MeasureCountData.TextColor ?? '',
                    }"
                >
                    {{ props.moduleData.MonthReportMetaInfo?.MeasureCountData.CountStatus }}
                </div>
            </div>
            <div
                class="status-box-inner body-2--regular"
                :style="{
                    backgroundColor: props.moduleData?.MonthReportMetaInfo?.EvaluationData.BackgroundColor ?? '',
                    color: props.moduleData?.MonthReportMetaInfo?.EvaluationData.TextColor ?? '',
                }"
                v-if="props.moduleData?.MonthReportMetaInfo?.EvaluationData"
                @click="handleClickStatus('CHANGE_EVALUATION')"
            >
                <div class="status-box-inner-item-label">{{ t('trend_report_report_status_change') }}</div>
                <div class="status-box-inner-item-text">
                    {{ props.moduleData.MonthReportMetaInfo?.EvaluationData.CountStatus }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatToMonthDay, subtractMonths, formatToYearMonth, parseDateSafely } from '@common/common/utils/timeUtils';
// import { formatToMonthDay, subtractMonths, formatToYearMonth } from '@common/common/utils/timeUtils';
import type { TimeSeriesResDto, TrendReportResDto } from '@common/openapi-client/openapi';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
    isAdmin: boolean;
    timeseriesData: TimeSeriesResDto | null;
    moduleData: TrendReportResDto | null;
    reportExistingEndDates: string[];
    currentSelectedEndDate: string;
    reportType: string;
    userUid?: string;
}>();

const emit = defineEmits<{
    (e: 'change:month', offset: number): void;
    (e: 'click:status', buttonType: string): void;
}>();

const firstReportDate = computed(() => props.reportExistingEndDates.at(0) ?? '');
const lastReportDate = computed(() => props.reportExistingEndDates.at(-1) ?? '');

const getDateFromEndDate = (endDateStr: string): Date => {
    const [y, m, d] = endDateStr.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d);
};

// TODO: 실서버 반영 시 주석 해제
const computedReportPeriod = computed(() => {
    const startDateStr = props.timeseriesData?.MonthReportMetaInfo?.StartDate;
    const endDateStr = props.timeseriesData?.MonthReportMetaInfo?.EndDate;

    if (!startDateStr || !endDateStr) return '';

    const startDate = parseDateSafely(startDateStr);
    const endDate = parseDateSafely(endDateStr);

    const startMonth = startDate.getMonth() + 1; // 0-based → 1-based
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    return `${startMonth}${t('all_month')} ${startDay}${t('all_day')} ~ ${endDay}${t('all_day')}`;
});

// const computedReportPeriod = computed(() => {
//     const startDateStr = props.currentSelectedEndDate;
//     if (!startDateStr) return '';

//     const [datePart] = startDateStr.split('T');
//     const [yearStr, monthStr, dayStr] = datePart.split('-');
//     const year = Number(yearStr);
//     const month = Number(monthStr); // 1-based

//     // 안전하게 Date 생성: JS는 0-based month이므로 -1
//     const date = new Date(year, month - 1, Number(dayStr));
//     date.setMonth(date.getMonth() - 1); // 1달 전

//     const newYear = date.getFullYear();
//     const newMonth = date.getMonth() + 1; // 0-based → 1-based
//     const startDay = 1;
//     const lastDay = new Date(newYear, newMonth, 0).getDate(); // 해당 월의 말일

//     return `${newMonth}${t('all_month')} ${startDay}${t('all_day')} ~ ${lastDay}${t('all_day')}`;
// });

const computedPublishDate = computed(() => {
    if (!props.moduleData?.MonthReportMetaInfo) {
        return t('all_none');
    }
    return formatToMonthDay(props.moduleData?.MonthReportMetaInfo?.PublishDate ?? '');
});

const computedReportTitle = computed(() => {
    const dateStr = props.currentSelectedEndDate;
    if (!dateStr) {
        return props.moduleData?.MonthReportMetaInfo?.Title ?? '';
    }

    try {
        // 한달을 뺀 날짜를 년월 형식으로 포맷팅
        const dateMinusOneMonth = subtractMonths(dateStr, 1);
        return formatToYearMonth(dateMinusOneMonth, t('all_year'), t('all_month'));
    } catch (error) {
        console.error('Date parsing error:', error);
        return '';
    }
});

const normalizeToYmd = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

const isPrevDisabled = computed(() => {
    if (props.reportType === 'Week') {
        return true;
    }

    const current = props.currentSelectedEndDate;
    const first = firstReportDate.value;

    if (!current || !first || props.reportExistingEndDates.length === 0) {
        return true;
    }

    const currentDate = normalizeToYmd(getDateFromEndDate(current));
    const firstDate = normalizeToYmd(getDateFromEndDate(first));

    if (props.isAdmin) {
        // 관리자: current가 first와 같거나 더 이전이면 비활성화
        return currentDate <= firstDate;
    } else {
        // 일반 사용자: current에서 한 달 빼서 비교
        const oneMonthBefore = new Date(currentDate);
        oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);

        return oneMonthBefore < firstDate;
    }
});

const isNextDisabled = computed(() => {
    if (props.reportType === 'Week') {
        return true;
    }

    const current = props.currentSelectedEndDate;
    const last = lastReportDate.value;

    if (!current || !last || props.reportExistingEndDates.length === 0) {
        return true;
    }

    const currentDate = normalizeToYmd(getDateFromEndDate(current));
    const lastDate = normalizeToYmd(getDateFromEndDate(last));
    const today = normalizeToYmd(new Date());

    if (props.isAdmin) {
        // 관리자: current가 last와 같거나 이후면 이동 불가
        return currentDate >= lastDate;
    } else {
        // reportExistingEndDates에 하나의 날짜만 있고 현재 날짜가 그 날짜와 같으면 다음 버튼 비활성화
        if (props.reportExistingEndDates.length === 1 && currentDate.getTime() === lastDate.getTime()) {
            return true;
        }

        // 년도와 월만 비교하기 위해 각 달의 1일로 정규화
        const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        if (currentMonth.getTime() === todayMonth.getTime()) {
            if (currentDate > lastDate) {
                return true;
            } else {
                return false;
            }
        }
        // 일반 사용자: current가 오늘보다 이후거나 같으면 이동 불가
        return currentDate >= today;
    }
});

const onClickPrev = () => {
    if (isPrevDisabled.value) return;
    emit('change:month', -1);
};

const onClickNext = () => {
    if (isNextDisabled.value) return;
    emit('change:month', 1);
};

const handleClickStatus = (buttonType: string) => {
    emit('click:status', buttonType);
};
</script>

<style scoped lang="scss">
.date-handle-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 1rem;

    .left-handle-icon {
        display: inline-block;
        width: 4.8rem;
        height: 2.6rem;
        background: url('../../../public/assets/images/ic_nav_arrow_black.png') no-repeat center center / 100% 100%;
        cursor: pointer;

        &.disabled {
            opacity: 0.1;
            pointer-events: none;
            cursor: default;
        }
    }

    .right-handle-icon {
        display: inline-block;
        width: 4.8rem;
        height: 2.6rem;
        background: url('../../../public/assets/images/ic_nav_arrow_black.png') no-repeat center center / 100% 100%;
        transform: rotate(180deg);
        cursor: pointer;

        &.disabled {
            opacity: 0.1;
            pointer-events: none;
            cursor: default;
        }
    }

    .date-text {
        color: $primary-black;
        word-break: keep-all;
        text-align: center;
    }
}

.date-info-box {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 1.6rem;

    .inspection-num-wrap {
        position: relative;

        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: 0.1rem;
            height: 1rem;
            background-color: $grey-5;
        }
        &::before {
            left: -0.4rem;
        }
        &::after {
            right: -0.4rem;
        }
    }

    p {
        margin: 0 0.4rem;
        font-size: 1.2rem;
        line-height: 150%;
    }

    .label {
        color: $grey-6;
    }

    .value {
        color: $grey-7;
    }
}

.status-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    margin-bottom: 1.5rem;
    padding: 0 1.5rem;

    .status-box-inner {
        width: calc(50% - 0.5rem);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem 0;
        background-color: #000;
        border-radius: 1rem;
        font-size: 1.5rem;
        cursor: pointer;

        .status-box-inner-item-label {
            line-height: 150%;
        }

        .status-box-inner-item-text {
            font-weight: 700;
            margin-left: 0.4rem;
        }
    }
}
</style>
